import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import useScrollDirection from 'hooks/useScrollDirection';

import { color } from 'asserts/styles/colors';
import { ReactComponent as Logo } from 'asserts/logo/logo_exbody.svg';
import { useAtomValue, useSetAtom } from 'jotai';
import userInforAtom from 'atom/userInfoAtom';
import { useMutation } from '@tanstack/react-query';
import { restfetcher } from 'queryClient';
import modalInformationAtom from 'atom/modalInformationAtom';
import { useResetAtom } from 'jotai/utils';
import companyInforAtom from 'atom/companyInfoAtom';

interface HeaderProps {
  labels: { title: string; link: string }[];
  isWhite?: boolean;
  selectedTab: number;
  onClickHeader?: () => void;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
}

export const Header = ({
  labels,
  isWhite = true,
  selectedTab,
  onClickHeader,
  setSelectedTab
}: HeaderProps) => {
  const [scrollDirection] = useScrollDirection('up');

  const navigate = useNavigate();

  const userInfor = useAtomValue(userInforAtom);
  const comanyInfor = useAtomValue(companyInforAtom);

  const setModalInformation = useSetAtom(modalInformationAtom);
  const resetUserInfoAtom = useResetAtom(userInforAtom);
  const resetCompanyInfoAtom = useResetAtom(companyInforAtom);

  const handleClickGoToJoinPage = () => {
    navigate('/join');
  };

  const handleClickMovePage = (index: number) => {
    const link = labels[index].link;

    if (index === 1 || index === 2) {
      if (userInfor.usridx !== 0) {
        if (index === 2) {
          navigate(link, { replace: true });
        } else {
          navigate(link);
        }
      } else {
        setModalInformation({
          type: 'LOGIN_CONFIRM',
          handleClickRightBtn: () => {
            navigate('/login');
          }
        });
      }
    } else {
      navigate(link);
    }

    if (index === 1) {
      if (userInfor.usridx !== 0) {
        setSelectedTab(index);
      }
    } else {
      setSelectedTab(index);
    }
  };

  const logoutApi = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'v1/auth/logout',
        params: {}
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        window.localStorage.clear();
        window.sessionStorage.clear();

        resetUserInfoAtom();
        resetCompanyInfoAtom();
        navigate('/');
      }
    },
    onError: (error: any) => {
      console.log(error);
    }
  });

  const handleClickLogOut = async () => {
    logoutApi.mutate();
  };

  return (
    <HeaderWrapper className={scrollDirection === 'up' ? 'nav active' : 'nav inactive'}>
      <Wrapper $isWhite={isWhite}>
        <RightWrapper $isWhite={isWhite}>
          <RightBtns>
            {comanyInfor.companyidx !== 0 ? (
              <>
                <div className="logout" onClick={handleClickLogOut}>
                  로그아웃
                </div>
                <Link to="/mypage">마이페이지</Link>
              </>
            ) : (
              <>
                <Link to="/login">로그인</Link>
                <Link to="join" className="join">
                  회원가입
                </Link>
              </>
            )}
          </RightBtns>
        </RightWrapper>
        <LeftWrapper $isWhite={isWhite}>
          <div>
            {!isWhite ? (
              <Logo fill="#fff" onClick={onClickHeader} />
            ) : (
              <Logo fill="#05054C" onClick={onClickHeader} />
            )}

            <ul>
              {labels.map((item, index) => (
                <TabItem
                  $isWhite={isWhite}
                  key={item.title}
                  $isSelected={selectedTab === index}
                  onClick={() => handleClickMovePage(index)}
                >
                  <p onClick={() => handleClickMovePage(index)}>{item.title}</p>
                </TabItem>
              ))}
            </ul>
          </div>
        </LeftWrapper>
      </Wrapper>
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.header`
  width: 100%;
  margin: 0 auto;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  transition: top 0.3s ease-in-out;

  &.active {
    top: 0;
  }

  &.inactive {
    top: -200px;
  }
`;

const Wrapper = styled.div<{ $isWhite?: boolean }>`
  background-color: ${(props) => !props.$isWhite && `${color.black}`};

  & ul {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 64px;
    margin: 0 auto;

    & li {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      font-size: 18px;
      font-weight: 500;
      color: ${(props) => (!props.$isWhite ? `${color.white}` : `${color.black2}`)};

      a {
        color: ${(props) => (!props.$isWhite ? `${color.white}` : `${color.black2}`)};
      }
    }
  }
`;

const LeftWrapper = styled.div<{ $isWhite?: boolean }>`
  position: relative;
  display: flex;
  height: 80px;
  background: ${(props) => (props.$isWhite ? `${color.white}` : `${color.black}`)};

  width: 100%;
  margin: 0 auto;

  & svg {
    position: absolute;
    margin: 24px auto 18px;
    cursor: pointer;
  }

  & div {
    display: flex;
    align-items: center;
    max-width: 1920px;
    min-width: 1280px;
    width: 100%;
    height: 100%;
    margin: 0 auto;
    padding: 0 48px;
  }

  & ul {
    margin: 28px auto 20px;
  }
`;

const TabItem = styled.li<{ $isSelected: boolean; $isWhite?: boolean }>`
  position: relative;
  padding-bottom: 8px;
  line-height: 22px;
  height: 32px;
  cursor: pointer;

  &::before,
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    width: 0;
    height: 2px;
    background-color: ${(props) => (!props.$isWhite ? `${color.white}` : `${color.black}`)};
    transition: width 0.3s;
  }

  &::before {
    left: 50%;
    width: ${(props) => (props.$isSelected ? '50%' : '0')};
  }

  &::after {
    right: 50%;
    width: ${(props) => (props.$isSelected ? '50%' : '0')};
  }

  &:hover:not(.selected)::before,
  &:hover:not(.selected)::after {
    width: 50%;
  }
`;

const RightWrapper = styled.div<{ $isWhite?: boolean }>`
  height: 32px;

  color: ${(props) => (props.$isWhite ? `${color.white}` : `${color.black2}`)};
  font-size: 13px;
  font-weight: 400;
  background: ${(props) => (props.$isWhite ? `${color.gray50}` : `${color.black}`)};
  width: 100%;
  a,
  p,
  div {
    color: ${(props) => (!props.$isWhite ? `${color.white}` : `${color.black2}`)};
  }

  a,
  div {
    cursor: pointer;
  }
`;

const RightBtns = styled.div`
  max-width: 1920px;
  min-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  align-items: center;
  gap: 33px;
  height: 100%;
  padding: 0 48px;

  & div:first-of-type {
    position: relative;
  }

  & div.join::before {
    content: '';
    position: absolute;
    top: 0;
    left: -16px;
    width: 1px;
    height: 12px;
    background: ${color.gray300};
  }

  & div.logout::before {
    content: '';
    position: absolute;
    top: 0;
    right: -16px;
    width: 1px;
    height: 12px;
    background: ${color.gray300};
  }
`;
